import React, { useState, useRef, useEffect } from 'react'
import TeethContainer from './TeethContainer'
import { allTeeth } from './ateeth'
import ToothDiagnoseModal from './ToothDiagnoseModal'
import {  useSelector } from 'react-redux'
import { RootState } from '../../../../../redux/store'
const rows = [
  { ranges: [[0, 5], [5, 10]], className: 'w-[calc((100%/16)*10)] flex flex-wrap gap-4 justify-between ml-auto mr-auto items-center mb-2 ' },
  { ranges: [[10, 18], [18, 26]], className: 'w-100 flex flex-wrap gap-2 justify-between items-center mb-2 ' },
  { ranges: [[26, 34], [34, 42]], className: 'w-100 flex flex-wrap gap-2 justify-between items-center mb-2' },
  { ranges: [[42, 47], [47, 52]], className: 'w-[calc((100%/16)*10)] flex flex-wrap gap-4 justify-between items-center mb-2 mr-auto ml-auto' },
]
function TeethCharts({ makeAction, setmakeAction }: any) {
  const advancedDiagnose = useSelector((state: RootState) => state.diagnose.advancedDiagnose);
  const [isOpen, setIsOpen] = useState(false)
  const [selectedTooth, setSelectedTooth] = useState<any>(null)
  const [rowWidths, setRowWidths] = useState<Record<number, number>>({})
  const [rowNumber, setrowNumber] = useState<any>(0)
  const rowContainerRefs = useRef<{ [key: number]: HTMLDivElement | null }>({})
  const [upsideDown, setupsideDown] = useState<boolean>(false)

  useEffect(() => {
    const updateRowWidths = () => {
      const next: Record<number, number> = {}
      rows.forEach((_, i) => {
        const container = rowContainerRefs.current[i]
        if (container) next[i] = container.offsetWidth
      })
      if (Object.keys(next).length > 0) setRowWidths(next)
    }
    updateRowWidths()
    const rafId = requestAnimationFrame(updateRowWidths)
    const resizeObserver = new ResizeObserver(updateRowWidths)
    rows.forEach((_, i) => {
      const el = rowContainerRefs.current[i]
      if (el) resizeObserver.observe(el)
    })
    return () => {
      cancelAnimationFrame(rafId)
      resizeObserver.disconnect()
    }
  }, [])

  // Function to merge API data with tooth object
  const mergeToothWithApiData = (tooth: any) => {
    // Filter out template objects (objects without actual toothNo values)
    const validAdvancedDiagnose = Array.isArray(advancedDiagnose)
      ? advancedDiagnose.filter((adv: any) => adv.toothNo !== undefined && typeof adv.toothNo === 'number')
      : [];

    const existingAdvancedDiagnose = validAdvancedDiagnose.length > 0
      ? validAdvancedDiagnose.find((adv: any) => adv.toothNo === tooth.toothNo)
      : null;

    if (!existingAdvancedDiagnose) {
      return tooth;
    }

    // Merge surface data from API
    let mergedSurface = tooth.surface || [];
    if (existingAdvancedDiagnose.surface && Array.isArray(existingAdvancedDiagnose.surface)) {
      // Map API surface data to tooth surface structure by sid
      mergedSurface = (tooth.surface || []).map((surf: any) => {
        const apiSurface = existingAdvancedDiagnose.surface.find(
          (s: any) => String(s.sid) === String(surf?.sid)
        );
        if (apiSurface) {
          return {
            ...surf,
            color: apiSurface.color || surf.color,
            diagnose: apiSurface.diagnose,
            diagnoseId: apiSurface.diagnoseId,
          };
        }
        return surf;
      });
    }

    // Map rootVariantDiagnoses to individualRootVariants
    let individualRootVariants: string[] = new Array(tooth.root?.length || 0).fill("plain");
    if (existingAdvancedDiagnose.rootVariantDiagnoses && Array.isArray(existingAdvancedDiagnose.rootVariantDiagnoses)) {
      existingAdvancedDiagnose.rootVariantDiagnoses.forEach((variant: any) => {
        if (variant.rootNo !== undefined && variant.diagnose) {
          const rootIndex = tooth.root?.findIndex((r: any) => String(r.rootNo) === String(variant.rootNo));
          if (rootIndex !== -1) {
            individualRootVariants[rootIndex] = variant.diagnose;
          }
        }
      });
    }

    // Create rootDiagnoses object for TeethContainer
    const rootDiagnosesData = {
      forceRedColor: false,
      hideRoot: existingAdvancedDiagnose.rootDiagnoses === "hide",
      useImplantSVG: existingAdvancedDiagnose.rootDiagnoses === "implant",
      rotate35: existingAdvancedDiagnose.rootDiagnoses === "left", // Left rotation -35deg
      rotateRight35: existingAdvancedDiagnose.rootDiagnoses === "right", // Right rotation +35deg
      individualRootVariants: individualRootVariants,
    };

    return {
      ...tooth,
      surface: mergedSurface,
      rootDiagnoses: rootDiagnosesData,
      condition: existingAdvancedDiagnose.condition, 
      bridge: existingAdvancedDiagnose.bridge,
    };
  };

  return (
    <div className='w-full bg-white rounded-lg p-6 teeth-chart-container'>
      <h3 className='text-sm font-bold text-slate-800 mb-4 text-center'>
        Advanced Teeth Chart
      </h3>
      <div className='flex flex-col gap-1 mb-4 text-xs'>
        <div className='flex items-center gap-2'>
          <div className='w-12 h-0.5 bg-[#06b6d4]'></div>
          <span className='text-gray-700'>Conventional Fixed</span>
        </div>
        <div className='flex items-center gap-2'>
          <div className='w-12 h-0.5 bg-[#ef4444]'></div>
          <span className='text-gray-700'>Resin Bonded</span>
        </div>
        <div className='flex items-center gap-2'>
          <div className='w-12 h-0.5 bg-[#a855f7]'></div>
          <span className='text-gray-700'>Cantilever</span>
        </div>

      </div>
      <div className='overflow-x-auto md:overflow-x-visible -mx-6 px-6 md:mx-0 md:px-0'>
        <div className='flex flex-col gap-2 min-w-[1280px] md:min-w-1280px'>
          {rows.map((row: any, i: number) => {
            const rowTeeth = row.ranges.flatMap(([start, end]: [number, number]) => allTeeth.slice(start, end));
            const rowToothNumbers = rowTeeth.map((t: any) => t.toothNo);
            return (
              <div
                key={i}
                ref={(el) => { rowContainerRefs.current[i] = el }}
                className={row.className + " relative"}
              >
                {row.ranges.map(([start, end]: [number, number], j: number) => (
                  <React.Fragment key={j}>
                    {allTeeth.slice(start, end).map((tooth: any) => {
                      const toothWithApiData = mergeToothWithApiData(tooth);
                      return (
                        <TeethContainer
                          key={tooth.toothNo}
                          className='cursor-pointer hover:scale-105 transition-all duration-300'
                          onClick={(e: any) => { setIsOpen(true); setSelectedTooth(tooth); setupsideDown(start > 20 ? true : false); setrowNumber(i) }}
                          rotate={start > 20 ? true : false}
                          tooth={toothWithApiData}
                          viewWidth={rowWidths[i] ?? 0}
                          rowToothNumbers={rowToothNumbers}
                        />
                      );
                    })}
                  </React.Fragment>
                ))}
                  <div className='flex w-[2px] h-[150px] bg-gray-300 absolute top-[50%] translate-y-[-50%] left-[50%] -translate-x-1/2'></div>

              </div>
            );
          })}
        </div>
      </div>
      {isOpen && <ToothDiagnoseModal isOpen={isOpen} onClose={() => { setIsOpen(false); }} rowNumber={rowNumber} tooth={selectedTooth} upsideDown={upsideDown} />}
    </div>
  )
}

export default TeethCharts